// src/utils/cmsContentLoader.js
/**
 * Utility functions to load content from DecapCMS JSON files
 * Falls back to hardcoded data if CMS files aren't available yet
 */
import React from 'react';

/**
 * Load JSON content from the /content directory
 * @param {string} path - Path relative to /content directory
 * @returns {Promise<Object>} - Parsed JSON content
 */
export const loadCMSContent = async (path) => {
  try {
    const publicUrl = process.env.PUBLIC_URL || '';
    // Remove leading slash from path if present to avoid double slashes
    const cleanPath = path.startsWith('/') ? path.slice(1) : path;
    const response = await fetch(`${publicUrl}/content/${cleanPath}`);
    if (!response.ok) {
      throw new Error(`Failed to load content from ${path}`);
    }
    return await response.json();
  } catch (error) {
    console.warn(`CMS content not found at ${path}, using fallback data`, error);
    return null;
  }
};

/**
 * Load site settings
 */
export const loadSiteSettings = async () => {
  return await loadCMSContent('settings/site.json');
};

/**
 * Load header section content
 */
export const loadHeaderContent = async () => {
  return await loadCMSContent('sections/header.json');
};

/**
 * Load bio section content
 */
export const loadBioContent = async () => {
  return await loadCMSContent('sections/bio.json');
};

/**
 * Load footer section content
 */
export const loadFooterContent = async () => {
  return await loadCMSContent('sections/footer.json');
};

/**
 * Load album info
 * @param {string} albumSlug - Album slug (feed-the-people, global-lighting-project, call-of-death)
 */
export const loadAlbumInfo = async (albumSlug) => {
  return await loadCMSContent(`albums/${albumSlug}/info.json`);
};

/**
 * Load album songs
 * @param {string} albumSlug - Album slug (feed-the-people, global-lighting-project, call-of-death)
 */
export const loadAlbumSongs = async (albumSlug) => {
  const data = await loadCMSContent(`albums/${albumSlug}/songs.json`);
  return data?.songs || [];
};

/**
 * Convert markdown lyrics to React elements matching the exact JSX structure from songDataFTP.js
 * Original structure: <div><p>lines with <br /></p><br /><p>next paragraph</p></div>
 * @param {string} markdown - Markdown formatted lyrics
 * @returns {Array} - Array of React elements matching original JSX format
 */
export const parseLyrics = (markdown) => {
  if (!markdown) return [];
  
  // Split by double newlines for paragraphs
  const paragraphs = markdown.split('\n\n');
  
  // Build JSX elements matching the original structure exactly
  const elements = [];
  
  paragraphs.forEach((para, idx) => {
    // Convert single newlines to <br /> tags within paragraph
    const lines = para.split('\n');
    
    elements.push(
      <p key={`p-${idx}`}>
        {lines.map((line, lineIdx) => (
          <React.Fragment key={lineIdx}>
            {line}
            {lineIdx < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    );
    
    // Add <br /> spacing between paragraphs (except after the last one)
    if (idx < paragraphs.length - 1) {
      elements.push(<br key={`br-${idx}`} />);
    }
  });
  
  // Wrap in single div container like the original
  return [<div key="lyrics-container">{elements}</div>];
};

/**
 * Merge songs with lyrics (maintains existing data structure pattern)
 * @param {Array} songs - Array of song objects
 * @returns {Array} - Songs with lyrics merged in
 */
export const mergeSongsWithLyrics = (songs) => {
  return songs.map(song => ({
    ...song,
    lyric: song.lyrics ? parseLyrics(song.lyrics) : []
  }));
};
